import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { get, trim } from 'lodash';
import { Controller, useForm } from 'react-hook-form';
import {
	SdfAlert,
	SdfBusyIndicator,
	SdfButton,
	SdfFocusPane,
	SdfInput,
	SdfTextarea
} from '@synerg/react-components';
import {
	useApplicationState,
	showErrorModal,
	updateCurrentDevProjectDetails,
	updateCurrentProdProjectDetails,
	setLoading
} from '../../../utilities/applicationState';
import { descriptionLength } from '../../common/util';

export const EditPartnerProject = (props = {}) => {
	const {
		projectDetail,
		showEditModal,
		setShowEditModal,
		handleResponse
	} = props;

	const defaultValues = {
		projectName: '',
		projectDescription: ''
	};

	const { control, handleSubmit, setValue, reset } = useForm(defaultValues);
	const { name, description, b2cConsumerAppID, b2bConsumerAppID, _id: projectID, type, ooid, associatedProject = {} } = projectDetail || {};

	// eslint-disable-next-line no-unused-vars
	const [store, dispatch] = useApplicationState();
	const { loading } = store;
	const [numberOfEndLineCharacter, setNumberOfEndLineCharacter] = useState(0);
	const [projectName, setProjectName] = useState(name || 'test_project');
	const [applicationNameList, setApplicationNameList] = useState([]);
	const [projectDescription, setProjectDescription] = useState(description);
	const [projectDescriptionLength, setProjectDescriptionLength] = useState(0);
	let projectNamePrime = '' + projectName;
	let projectDescriptionPrime = '' + projectDescription;

	const updateProjectDescriptionLength = (value) => {
		const { endLineChar, lengthWithoutEndlineChar } = descriptionLength(value);
		setNumberOfEndLineCharacter(endLineChar);
		setProjectDescriptionLength(lengthWithoutEndlineChar);
	};

	const close = () => setShowEditModal(false);

	const ShowError = ({ message }) => {
		return (
			<SdfAlert
				slot="alert"
				size="sm"
				status="error"
				type="inline"
			>
				{message}
			</SdfAlert>
		);
	};

	const closeModal = () => {
		setShowEditModal(false);
		reset(defaultValues);
	};

	const validate = {
		tooShort: value => (trim(value).length >= 3) || 'App name too short',
		tooLong: value => (trim(value).length <= 40) || 'App name too long',
		inValidCharacters: value => !(/[^A-Za-z0-9-. ]/.test(trim(value))) || 'Valid characters permitted are A-Z, a-z, 0-9, period (.), dash (-), and spaces',
		nameTaken: value => {
			const pnt = trim(value);
			return !(applicationNameList.some(name =>
				name.replace(/(_DEV|_delete_DEV)? - (Application|Data connector)/ig, '').toLowerCase() ===
				pnt.replace(/(_DEV|_delete_DEV)? - (Application|Data connector)/ig, '').toLowerCase()
			)) || 'App name is taken';
		}
	};


	const updateData = async (newState) => {
		if (newState.projectName === projectNamePrime && newState.projectDescription === projectDescriptionPrime) {
			close();
		} else {
			dispatch(setLoading(true));
			try {
				const { data } = await axios.post(`${import.meta.env.VITE_ROOT_PATH}/api/project/name-description`, {
					projectID, ...newState, ooid
				});
				if (type === 'PROD') {
					dispatch(updateCurrentProdProjectDetails({ name: data.name, description: data.description }));
				}
				if (type === 'DEV') {
					dispatch(updateCurrentDevProjectDetails({
						name: data.name,
						description: data.description,
						updatedProjectName: data.name
					}))
				}
				handleResponse('Successfully updated project details!', 'success');
			} catch (e) {
				const errorMessage = get(e, 'response.data.message', 'Something went wrong when trying to update project name and description. Please try again.');
				handleResponse(errorMessage);
			}
			setShowEditModal(false);
			dispatch(setLoading(false));
		}
	};


	useEffect(() => {
		setProjectName(name);
		setProjectDescription(description);
	}, [name, description]);

	useEffect(() => {
		if (projectDescription) {
			updateProjectDescriptionLength(projectDescription);
		}
	}, [projectDescription]);

	useEffect(() => {
		let mounted = true;
		async function getAppNames() {
			if (!b2cConsumerAppID) {
				return;
			}
			try {
				let queryParamList = '?';
				if (b2cConsumerAppID) {
					queryParamList += `withoutb2c=${b2cConsumerAppID}`;
				}
				if (b2bConsumerAppID) {
					queryParamList += (queryParamList.length > 1 ? '&' : '') + `withoutb2b=${b2bConsumerAppID}`;
				}
				if (associatedProject.b2cConsumerAppID) {
					queryParamList += (queryParamList.length > 1 ? '&' : '') + `withoutProdb2c=${associatedProject.b2cConsumerAppID}`;
				}
				if (associatedProject.b2bConsumerAppID) {
					queryParamList += (queryParamList.length > 1 ? '&' : '') + `withoutProdb2b=${associatedProject.b2bConsumerAppID}`;
				}
				if (ooid) {
					queryParamList += (queryParamList.length > 1 ? '&' : '') + `ooid=${ooid}`;
				}
				const appNameResponse = await axios(`${import.meta.env.VITE_ROOT_PATH}/api/organization/applications/name${queryParamList}`);
				if (appNameResponse && appNameResponse.data && mounted) {
					setApplicationNameList(appNameResponse.data);
				}
			} catch (e) {
				const errorMessage = get(e, 'response.data.message', 'Something went wrong when trying to get application name list. Please try again.');
				dispatch(showErrorModal(errorMessage));
			}
		}
		getAppNames();
		return () => mounted = false;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [name, b2cConsumerAppID]);

	useEffect(() => {
		if (showEditModal) {
			setValue('projectName', name);
			setValue('projectDescription', description);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [showEditModal]);


	return (
		<SdfFocusPane
			visible={showEditModal}
			closeable={true}
			hideDismissButton={true}
        	hideAcceptButton={true}
			onSdfAccept={e => {
				handleSubmit(data => updateData(data))();
			}}
			onSdfDismiss={() => {
				closeModal();
			}}
			dismissOnClickOutside={true}
			spacing={"loose"}
			id="sdf-modal"
			size="md"
		>
			{loading &&
				<SdfBusyIndicator
					style={{ color: "#1a8099" }}
					size="xl"
					fullScreen={true}
					overlay={true}
				/>
			}

			<div className='ml-12'>
				<span slot='title' className='text-heading-07 font-black'>Edit project</span>
				<div slot='page-content' className='pr-24'>
					<div className='min-h-md'>
						<div className='pb-10'>
							<Controller
								name="projectName"
								control={control}
								rules={{
									validate: validate
								}}
								render={({
									field: { value, onChange },
									fieldState: { error }
								}) => (
									<div>
										<SdfInput
											name="projectName"
											label="Project name"
											className="mt-5 w-full"
											onSdfInput={(event) => {
												onChange(event.detail);
											}}
											value={value}
											state={error ? 'error' : 'normal'}
											maxLength={40}
										>
											{error && error.message && <ShowError message={error.message} />}
										</SdfInput>
									</div>
								)}
							/>
						</div>
						<div>
							<Controller
								name="projectDescription"
								control={control}
								rules={{
									validate: validate.inValidCharacters
								}}
								render={({
									field: { value, onChange },
									fieldState: { error },
								}) => (
									<SdfTextarea
										name="projectDescription"
										label="Project Description"
										rows={6}
										onSdfInput={event => { onChange(event.detail); updateProjectDescriptionLength(event.detail) }}
										id="sdf-project-desc"
										placeholder={"Description"}
										value={value}
										maxlength={256 + numberOfEndLineCharacter}
										state={error ? 'error' : 'normal'}
									>
										{error && error.message &&
											<ShowError message={error.message} />
										}
									</SdfTextarea>
								)}
							/>
							<div className='mt-2 text-right'>{projectDescriptionLength}/256</div>
						</div>
					</div>
				</div>
			</div>

			<SdfButton
              slot="custom-buttons"
              onClick={(e) => {
                closeModal();
              }}
            >
              Cancel
            </SdfButton>
            <SdfButton
              slot="custom-buttons"
              emphasis="primary"
              onClick={(e) => {handleSubmit((data) => updateData(data))();}}
            >
              Save
            </SdfButton>
		</SdfFocusPane>
	);
};
